import React, { useState, DragEvent, ChangeEvent, useRef, useEffect } from 'react';
import { Button } from '../components/ui/button';
import CountdownOverlay from '../components/CountdownOverlay';
import { playBeatstage, BeatstagePlayer } from '../playBeatstage';
import { decodeBundle } from '../utils/decodeBundle';
import { GameClock, emitBeat } from '../timing';
import { runWorker } from '../worker-messages';
import { mods } from '../mods';
import CalibrationWidget from '../components/CalibrationWidget';

export default function Play() {
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [playing, setPlaying] = useState(false);
  const [failed, setFailed] = useState(false);
  // Container for the Beatstage player. This element is managed outside of React
  // to avoid conflicts when the player manipulates the DOM directly.
  const containerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<BeatstagePlayer | null>(null);
  const [chart, setChart] = useState<{ encoded: string; audio: Blob | string } | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [dots, setDots] = useState('.');
  const [countdown, setCountdown] = useState<{
    ctx: AudioContext;
    startAt: number;
    beatDuration: number;
  } | null>(null);
  const [showCal, setShowCal] = useState(false);

  // Enable easy mod by default when entering the /play route
  useEffect(() => {
    for (const key of Object.keys(mods) as (keyof typeof mods)[]) {
      delete mods[key];
    }
    mods.easy = true;
  }, []);

  useEffect(() => {
    if (!loading) return;
    const seq = ['.', '..', '...', '.', '..', '..'];
    setDots(seq[0]);
    let i = 0;
    const id = setInterval(() => {
      i = (i + 1) % seq.length;
      setDots(seq[i]);
    }, 500);
    return () => clearInterval(id);
  }, [loading]);

  useEffect(() => {
    if (!playing) return;
    const beatMs = 60000 / 120;
    let beat = 0;
    const interval = setInterval(() => {
      emitBeat(beat++);
      playerRef.current?.shakeBackground();
    }, beatMs);
    return () => clearInterval(interval);
  }, [playing]);

  const handleFiles = async (files: FileList | null) => {
    const file = files && files[0];
    if (!file) return;
    setError(null);
    setLoading(true);
    setChart(null);
    console.log('[Play] file name', file.name, 'size', file.size, 'type', file.type);
    const buffer = await file.arrayBuffer();
    console.log('[Play] buffer length', buffer.byteLength);
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    let encoded = btoa(binary);
    console.log('[Play] encoded length', encoded.length);
    try {
      const bundle = await decodeBundle(encoded);
      console.log('[Play] bundle meta', bundle.meta);
      let audio: Blob | string;
      const audioMeta: any = bundle.meta?.audio;
      if (audioMeta?.data) {
        try {
          const binary = atob(audioMeta.data);
          const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
          audio = new Blob([bytes], { type: audioMeta.mime || 'audio/mpeg' });
        } catch (err) {
          console.error('[Play] failed to decode embedded audio', err);
          throw err;
        }
      } else if (bundle.audio?.data) {
        try {
          const binary = atob(bundle.audio.data);
          const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
          audio = new Blob([bytes], { type: 'audio/mpeg' });
        } catch (err) {
          console.error('[Play] failed to decode embedded audio', err);
          throw err;
        }
      } else {
        if (!audioMeta?.hash) throw new Error('Missing audio reference in chart');
        audio = `https://files.beatstage.net/audio/${audioMeta.hash}`;
      }
      for (const key of Object.keys(mods) as (keyof typeof mods)[]) {
        delete mods[key];
      }
      mods.easy = true;
      if (bundle.chart && bundle.timing?.segments) {
        const { hitObjects } = await runWorker({
          type: 'buildHitObjects',
          chart: bundle.chart,
          segments: bundle.timing.segments,
        });
        bundle.hitObjects = hitObjects;
        const { encoded: reencoded } = await runWorker({ type: 'serialize', bundle });
        encoded = reencoded;
      }
      setChart({ encoded, audio });
    } catch (err) {
      console.error('[Play] failed to load .beatstage', err);
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };


  const startPlaying = async (): Promise<void> => {
    const container = containerRef.current;
    if (!chart || !container) return;
    playerRef.current?.destroy();
    container.innerHTML = '';
    const ctx = GameClock.context;
    if (!ctx) return;
    if (ctx.state === 'suspended') await ctx.resume();
    const beatDuration = 1;
    const startAt = ctx.currentTime + 3 * beatDuration;
    setCountdown({ ctx, startAt, beatDuration });
    try {
      const player = await playBeatstage(
        chart.encoded,
        chart.audio,
        container,
        false,
        undefined,
        undefined,
        startAt,
        () => {
          setFailed(true);
          setPlaying(false);
        },
      );
      playerRef.current = player;
      setPlaying(true);
    } catch (err) {
      console.error('[Play] failed to load .beatstage', err);
      setPlaying(false);
      setError((err as Error).message);
    }
  };

  const handleCountdownComplete = async (): Promise<void> => {
    setCountdown(null);
  };

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    void handleFiles(e.dataTransfer.files);
  };

  const onDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(true);
  };

  const onDragLeave = () => setDragging(false);

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    void handleFiles(e.target.files);
  };

  if (error) {
    return <div className="p-4 text-red-500">{error}</div>;
  }

  return (
    <div className="w-screen h-screen flex items-center justify-center">
      <div className="relative flex flex-col items-center justify-center gap-4 w-full h-full">
        <div
          ref={containerRef}
          className="absolute inset-0"
          style={{ pointerEvents: playing ? 'auto' : 'none' }}
        />
        {countdown && (
          <CountdownOverlay
            ctx={countdown.ctx}
            startAt={countdown.startAt}
            beatDuration={countdown.beatDuration}
            onComplete={handleCountdownComplete}
            fullScreen={false}
          />
        )}
        {failed && (
          <div
            className="absolute inset-0 z-10 flex cursor-pointer items-center justify-center bg-black/70 text-white"
            onClick={() => {
              setFailed(false);
              void startPlaying();
            }}
          >
            <span className="text-6xl font-bold">Failed</span>
          </div>
        )}
        {!chart && !playing && !loading && (
          <>
            <div
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
              className={`flex items-center justify-center w-80 h-40 border-2 border-dashed rounded ${dragging ? 'bg-accent/20' : ''}`}
            >
              <span>Drop .beatstage file here</span>
            </div>
            <input
              id="beatstage-input"
              type="file"
              accept=".beatstage"
              className="hidden"
              onChange={onChange}
            />
            <label htmlFor="beatstage-input">
              <Button>Upload .beatstage</Button>
            </label>
          </>
        )}
        {loading && !playing && <div>Loading{dots}</div>}
        {chart && !playing && !loading && !countdown && !failed && (
          <Button onClick={startPlaying}>Play</Button>
        )}
        {!playing && !loading && !countdown && !failed && (
          <Button variant="outline" onClick={() => setShowCal(true)}>
            Calibrate
          </Button>
        )}
        {showCal && <CalibrationWidget onComplete={() => setShowCal(false)} />}
      </div>
    </div>
  );
}
